import type {
  Pipeline,
  Step,
  ColorChannel,
  GradientBand,
  PreviewBlock,
} from './types';
import {
  asColorSeparateParams,
  asGradientSepParams,
  newStepId,
} from './tree';

// Reconcile virtual `channel` / `band` children of a fan-out parent with the
// parent's `channels` / `bands` parameter array.
//
// Behavior:
//   - For each parent param entry, ensure a matching child Step exists, in the
//     same order.
//   - Children whose corresponding param entry was removed are deleted (along
//     with their subtree).
//   - The virtual child's name follows the param entry's name unless the user
//     has manually overridden it. (v1: param-entry name and child name are
//     kept in lockstep — renaming the param renames the child, and vice versa
//     happens through the channel/band UI which writes to the param.)
//   - The child's `channel_index` / `band_index` is updated to match its
//     position in the array (so reordering "just works").

function getOrCreateVirtualChild(
  pipeline: Pipeline,
  parent: Step,
  childType: 'channel' | 'band',
  desiredName: string,
  index: number,
): string {
  // First check if a child with a matching name already exists; reuse it.
  for (const childId of parent.children) {
    const c = pipeline.steps[childId];
    if (c && c.type === childType && c.name === desiredName) {
      return childId;
    }
  }
  // Otherwise create new.
  const id = newStepId();
  const step: Step = {
    id,
    type: childType,
    name: desiredName,
    params:
      childType === 'channel'
        ? { channel_index: index }
        : { band_index: index },
    children: [],
  };
  pipeline.steps[id] = step;
  return id;
}

function deleteSubtree(pipeline: Pipeline, stepId: string): void {
  const step = pipeline.steps[stepId];
  if (!step) return;
  for (const c of [...step.children]) deleteSubtree(pipeline, c);
  delete pipeline.steps[stepId];
}

export function syncColorSeparateChildren(
  pipeline: Pipeline,
  parentId: string,
): void {
  const parent = pipeline.steps[parentId];
  if (!parent) return;
  const params = asColorSeparateParams(parent);
  if (!params) return;

  const wantedIds: string[] = [];
  const existingChannels = new Map<string, string>(); // name -> child id
  for (const childId of parent.children) {
    const c = pipeline.steps[childId];
    if (c && c.type === 'channel') existingChannels.set(c.name, childId);
  }

  params.channels.forEach((ch: ColorChannel, idx: number) => {
    let childId = existingChannels.get(ch.name);
    if (childId) {
      const step = pipeline.steps[childId];
      if (step) {
        (step.params as { channel_index: number }).channel_index = idx;
        if (!step.preview) {
          step.preview = makeDefaultChannelPreview(ch.target);
        }
      }
      existingChannels.delete(ch.name);
    } else {
      childId = getOrCreateVirtualChild(
        pipeline,
        parent,
        'channel',
        ch.name,
        idx,
      );
      const created = pipeline.steps[childId];
      if (created && !created.preview) {
        created.preview = makeDefaultChannelPreview(ch.target);
      }
    }
    wantedIds.push(childId);
  });

  // Anything in `existingChannels` that survived is now orphaned.
  for (const orphanId of existingChannels.values()) {
    deleteSubtree(pipeline, orphanId);
  }

  parent.children = wantedIds;
}

export function syncGradientSepChildren(
  pipeline: Pipeline,
  parentId: string,
): void {
  const parent = pipeline.steps[parentId];
  if (!parent) return;
  const params = asGradientSepParams(parent);
  if (!params) return;

  const wantedIds: string[] = [];
  const existingBands = new Map<string, string>();
  for (const childId of parent.children) {
    const c = pipeline.steps[childId];
    if (c && c.type === 'band') existingBands.set(c.name, childId);
  }

  params.bands.forEach((b: GradientBand, idx: number) => {
    let childId = existingBands.get(b.name);
    if (childId) {
      const step = pipeline.steps[childId];
      if (step) {
        (step.params as { band_index: number }).band_index = idx;
        // `band` is a leaf-producing virtual node. Provide a default preview
        // block if missing.
        if (!step.preview) {
          step.preview = makeDefaultBandPreview(idx, params.bands.length);
        }
      }
      existingBands.delete(b.name);
    } else {
      childId = getOrCreateVirtualChild(
        pipeline,
        parent,
        'band',
        b.name,
        idx,
      );
      const created = pipeline.steps[childId];
      if (created) {
        created.preview = makeDefaultBandPreview(idx, params.bands.length);
      }
    }
    wantedIds.push(childId);
  });

  for (const orphanId of existingBands.values()) {
    deleteSubtree(pipeline, orphanId);
  }

  parent.children = wantedIds;
}

function makeDefaultChannelPreview(targetHex: string): PreviewBlock {
  return {
    color: targetHex,
    ink_mode: 'opaque',
    opacity: 100,
    enabled: true,
  };
}

function makeDefaultBandPreview(
  index: number,
  total: number,
): PreviewBlock {
  // Map band index to an evenly-spaced grey: band 0 (darkest range) → dark
  // grey, last band → light grey.
  const t = total <= 1 ? 0 : index / (total - 1);
  const v = Math.round(40 + t * (215 - 40)); // span 40..215
  const hex = v.toString(16).padStart(2, '0');
  return {
    color: `#${hex}${hex}${hex}`,
    ink_mode: 'opaque',
    opacity: 100,
    enabled: true,
  };
}

// Convenience: walk pipeline and run sync on every fan-out parent. Cheap.
export function syncAllVirtualChildren(pipeline: Pipeline): void {
  for (const [id, step] of Object.entries(pipeline.steps)) {
    if (step.type === 'color_separate') {
      syncColorSeparateChildren(pipeline, id);
    } else if (step.type === 'gradient_sep') {
      syncGradientSepChildren(pipeline, id);
    }
  }
}
